/**
 * src/components/mfa/setupFlow.ts
 * アシスタントの「はじめての追加認証の設定」の流れを決める純粋な関数（画面・通信なし）。
 * 画面（ステップ式／ダイアログ）は、ここの判断だけを使う。サーバーの検証条件は一切変えない。
 *   scripts/mfa-test/setup_flow_test.mjs で単体テストする（node の型除去で直接読み込めるよう、型注釈だけを使う）。
 */

export type MfaMethodName = 'none' | 'passkey' | 'email'
export type MethodChoice = 'passkey' | 'email'
/** password = 先に初期パスワードの変更 / email = STEP1 / choose = STEP2 / passkey,emailMethod = STEP3 */
export type SetupView = 'password' | 'email' | 'choose' | 'passkey' | 'emailMethod'

export type SetupInput = {
  isAssistant: boolean
  method: MfaMethodName
  emailVerified: boolean
  emailPlaceholder: boolean
  mustChangePassword: boolean
}

/** ステップ式の設定画面を出すか: アシスタントで、まだ方式を選んでいない（未設定） */
export function isFirstSetup(i: SetupInput): boolean {
  return i.isAssistant && i.method === 'none'
}

/** メールアドレスが本人のものとして確認済みか（仮アドレスは確認済みにならない） */
export function emailReady(i: Pick<SetupInput, 'emailVerified' | 'emailPlaceholder'>): boolean {
  return i.emailVerified && !i.emailPlaceholder
}

/**
 * 最初に出す画面。初期パスワードの変更が先、次にメール確認（未確認のときだけ。飛ばせない）、確認済みなら方式の選択。
 * 設定の途中でメール確認を二度求めないよう、STEP1 を飛ばす選択肢は設けない（やめる＝設定全体を閉じる）。
 */
export function startView(i: SetupInput): SetupView {
  if (i.mustChangePassword) return 'password'
  return emailReady(i) ? 'choose' : 'email'
}

/**
 * 方式を選ぶ／追加するとき、メール確認のダイアログを先に出すか。
 *   ステップ式では STEP1 で確認済みになっているので出ない（二重の案内にならない）。
 *   「認証方法を変更・管理する」など、確認が済んでいない状態で操作したときの安全網として出す。
 *   管理者は対象外（メール確認を使わない）。
 */
export function needsEmailGate(i: { isAssistant: boolean; emailVerified: boolean; emailPlaceholder: boolean }): boolean {
  return i.isAssistant && !emailReady(i)
}

export type DeviceKind = 'phone' | 'desktop'

/** 使っている端末の種類（スマホ／パソコン）。案内の出し分けにだけ使う */
export function deviceKind(userAgent: string): DeviceKind {
  return /iPhone|iPod|iPad|Android/i.test(userAgent) ? 'phone' : 'desktop'
}

export type CodeProblem = 'expired' | 'attempts' | 'none' | 'wrong' | 'other'

/** 確認コードのエラーコード → 画面の案内の種類（期限切れ・回数超過・コード未発行は「再送」を勧める） */
export function codeProblem(code: string | undefined): CodeProblem {
  switch (code) {
    case 'OTP_EXPIRED': return 'expired'
    case 'OTP_TOO_MANY_ATTEMPTS': return 'attempts'
    case 'OTP_NO_CHALLENGE': return 'none'
    case 'OTP_WRONG_CODE': return 'wrong'
    default: return 'other'
  }
}

export function needsResend(p: CodeProblem): boolean {
  return p === 'expired' || p === 'attempts' || p === 'none'
}

/** ログインIDが変わるか（メールアドレスはログインID。大文字小文字は区別しない） */
export function loginIdWillChange(currentEmail: string, typed: string): boolean {
  const a = currentEmail.trim().toLowerCase()
  const b = typed.trim().toLowerCase()
  return b.length > 0 && a !== b
}

/** パスキー登録後の「使えるか確認」の状態: 一度でも、そのパスキーでログインした記録があるか */
export function passkeyLoginConfirmed(passkeys: Array<{ last_used_at: string | null; usable?: boolean }>): boolean {
  return passkeys.some((p) => p.usable !== false && !!p.last_used_at)
}

export type StorageHint = 'synced' | 'local'

/**
 * 登録したパスキーの保存先の「目安」。ブラウザ／OSが返す情報（同期されるか）だけから推測するので、断定はしない。
 *   synced = iCloud キーチェーン・Google パスワードマネージャなどで同期される（スマホで登録した場合は通常こちら）
 *   local  = その端末の中だけ（パソコンの Windows Hello・同期をオフにしたスマホ・セキュリティキーなど）
 */
export function storageHint(p: { backed_up: boolean; device_type?: string | null }): StorageHint {
  return p.backed_up || p.device_type === 'multiDevice' ? 'synced' : 'local'
}

/** 完了画面で出す注意: パソコンで登録したのに、同期されない（その端末の中だけ）目安のとき */
export function showLocalOnDesktopWarning(kind: DeviceKind, hint: StorageHint): boolean {
  return kind === 'desktop' && hint === 'local'
}
